import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Form, Select } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { createLoadingSelector } from 'erisxkit/client';
import _ from 'lodash';
import { fetchUsers, getUserCollection } from '../../reducers/usersReducer';

/**
 * Filtered Member User Select Component
 *
 * GCP-828: Fixes redux cache pollution by filtering cached users client-side
 * before rendering dropdown options. Only shows users matching allowedTypes.
 *
 * @param {string|string[]} allowedTypes - Member user type(s) to allow (e.g., 'member_user' or ['member_user', 'firm_admin'])
 * @param {function} onChange - Callback when selection changes
 * @param {string|string[]} value - Current selected value(s)
 * @param {boolean} multiple - Whether multiple selection is allowed
 * @param {string} label - Label for the field
 * @param {string} placeholder - Placeholder text
 * @param {string} name - Name attribute for the select (used for testing/identification)
 */
const FilteredMemberUserSelect = ({
  allowedTypes,
  onChange,
  value,
  multiple,
  label,
  placeholder,
  name,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector(createLoadingSelector(['USERS']));

  // Normalize allowedTypes to always be an array
  const allowedTypesArray = Array.isArray(allowedTypes) ? allowedTypes : [allowedTypes];

  // Get all cached users and filter client-side to only allowed types
  const options = useSelector((state) => {
    const usersById = getUserCollection(state);
    const allUsers = Object.values(usersById);

    // Filter to only allowed types
    const filtered = allUsers.filter(user =>
      allowedTypesArray.includes(user.memberUserType)
    );

    // Transform to dropdown options
    return filtered.map(user => ({
      key: user.userId,
      text: user.email,
      value: user.userId,
      description: user.userId,
    }));
  });

  const handleSearchChange = useMemo(
    () => _.debounce((e, { searchQuery }) => {
      dispatch(
        fetchUsers({
          filter: [
            { attr: 'email', op: 'contain', value: searchQuery },
            { attr: 'type', op: 'eq', value: allowedTypesArray },
          ],
          limit: 10,
        }),
      );
    }, 500),
    [dispatch, allowedTypesArray]
  );

  return (
    <Form.Field
      control={Select}
      search
      clearable
      selection
      multiple={multiple}
      options={options}
      onChange={(e, { value }) => onChange(value)}
      value={value}
      onSearchChange={handleSearchChange}
      loading={loading}
      noResultsMessage="Search for a user."
      placeholder={placeholder}
      label={label}
      name={name}
    />
  );
};

FilteredMemberUserSelect.propTypes = {
  allowedTypes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  multiple: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
};

FilteredMemberUserSelect.defaultProps = {
  value: null,
  multiple: false,
  label: 'Users',
  placeholder: 'Select users',
  name: undefined,
};

export default FilteredMemberUserSelect;
